/**
 * 계정 관리 API — 관리 콘솔(8444)에 들어올 수 있는 사람 명단.
 *
 * ★★ 2026-08-25 **표 분리** — 콘솔 계정의 비밀번호·등급은 `admin_console_accounts` 가 갖는다.
 *   8443 통합설정 › 사용자 관리에서 같은 사람의 권한·비밀번호를 만져도 콘솔은 영향을 받지 않는다
 *   (그전에는 권한을 내리면 콘솔에 못 들어갔다 — 2026-08-25 실제 사고).
 *
 * 서버 SSOT: `admin/backend/src/routes/console-accounts.ts` (BFF 소유 표 `admin_console_accounts`).
 * chat-server 로 흘러가는 `/api/admin/*` 프록시가 아니라 **BFF 가 직접 답하는 경로**다.
 *
 * ★ 콘솔 계정은 **여기서 만들고 여기서 고친다** — '사용자 관리' 목록에서 골라 오지 않는다.
 *   계정의 실체(생성·프로필·권한·비밀번호)는 BFF 가 chat-server 에 위임하고, 화면은 한 번만 부른다.
 */
import { apiClient } from './client';

export interface ConsoleAccount {
  user_id: string;
  institution_id: string | null;
  /** 등록 당시 스냅샷. 화면은 살아 있는 값(user_email)을 먼저 쓴다. */
  email: string;
  name: string;
  is_active: boolean;
  note: string | null;
  created_at: string;
  updated_at: string;
  /**
   * 콘솔 등급 (2026-08-25 표 분리) — **콘솔 토큰의 role 이 이 값이다**.
   * 회사 쪽 역할(`user_role`)과 달라도 되고, 8443 에서 그걸 바꿔도 콘솔은 흔들리지 않는다.
   */
  console_role: string;
  /** 콘솔 비밀번호가 지정돼 있나. 해시는 서버 밖으로 나오지 않는다. */
  password_set: boolean;
  /** 운영용 계정 — 8443 회사 직원 목록에서 감춘다(2026-08-25). */
  is_service_account: boolean;
  /** users 조인 — 앵커 계정의 지금 상태(참고용). 로그인 판정에는 쓰이지 않는다. */
  user_role: string | null;
  user_is_active: boolean | null;
  user_email: string | null;
  user_name: string | null;
  user_deleted: boolean;
  last_login_at: string | null;
  /**
   * 콘솔 2차 인증을 등록했나 (2026-10-15 콘솔 계정 분리). 콘솔 자체 인증(standalone)일 때만 온다 —
   * 이 칸이 있으면 화면은 «콘솔 계정이 사람 계정과 분리된» 상태로 그린다(사번·직원 목록 칸이 없다).
   */
  mfa_enrolled?: boolean;
  /** 관리자가 2단계 인증을 «꺼 둔» 계정 — 로그인 때 QR·번호를 묻지 않는다(2026-10-15). */
  mfa_disabled?: boolean;
}

export type ConsoleRole = 'ADMIN' | 'SYSTEM_ADMIN';

export interface ConsoleAccountCreate {
  name: string;
  email: string;
  role: ConsoleRole;
  password: string;
  employeeNumber?: string;
  note?: string;
  /**
   * 회사 직원 목록(8443)에 보일지. **안 보내면 감춘다** — 콘솔 계정은 보통 운영용이라
   * 직원 명부에 섞이면 사람이 헷갈린다. 실제 직원이 콘솔도 쓰는 경우에만 켠다.
   */
  showInDirectory?: boolean;
}

export interface ConsoleAccountPatch {
  name?: string;
  email?: string;
  role?: ConsoleRole;
  password?: string;
  isActive?: boolean;
  note?: string;
  showInDirectory?: boolean;
}

export async function listConsoleAccounts(): Promise<{ items: ConsoleAccount[]; activeCount: number }> {
  const { data } = await apiClient.get<{ items: ConsoleAccount[]; activeCount: number }>('/admin/console-accounts');
  return { items: Array.isArray(data?.items) ? data.items : [], activeCount: data?.activeCount ?? 0 };
}

/** 신규 등록 — 계정 생성 + 명단 등록 + 비밀번호 지정을 서버가 한 번에 한다. */
export async function createConsoleAccount(body: ConsoleAccountCreate): Promise<{ userId: string }> {
  const { data } = await apiClient.post<{ ok: boolean; userId: string }>('/admin/console-accounts', body);
  return { userId: data?.userId ?? '' };
}

export async function updateConsoleAccount(userId: string, patch: ConsoleAccountPatch): Promise<void> {
  await apiClient.patch(`/admin/console-accounts/${userId}`, patch);
}

/** 명단에서 빼기 — 계정 자체는 남는다(계정 삭제는 사용자 관리의 몫). */
export async function removeConsoleAccount(userId: string): Promise<void> {
  await apiClient.delete(`/admin/console-accounts/${userId}`);
}

/** 2차 인증 초기화 — 휴대폰을 잃었을 때. 그 사람은 다음 로그인에서 다시 등록한다(standalone 만). */
export async function resetConsoleAccountMfa(userId: string): Promise<void> {
  await apiClient.post(`/admin/console-accounts/${userId}/mfa-reset`);
}

/** 2단계 인증 끄기·켜기 (2026-10-15) — 켜면 그 사람은 지금 세션이 끊기고 다음 로그인에서 QR 등록부터 한다. */
export async function setConsoleAccountMfa(userId: string, enabled: boolean): Promise<void> {
  await apiClient.post(`/admin/console-accounts/${userId}/mfa`, { enabled });
}
